import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clearCache } from "@/lib/cache";
import { api } from "./api";

const AuthCtx = createContext(null);

// Hydrate tenant from localStorage so Layout can render the correct logo on
// the very first paint — eliminating the "RecruitSift logo flashes for a
// frame then swaps to tenant logo" flicker across SPA nav AND browser reloads.
const TENANT_CACHE_KEY = "tp_tenant_cache";
function readCachedTenant() {
  try {
    const raw = localStorage.getItem(TENANT_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function writeCachedTenant(t) {
  try {
    if (t) localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify({ id: t.id, name: t.name, logo_data_url: t.logo_data_url || null }));
    else localStorage.removeItem(TENANT_CACHE_KEY);
  } catch { /* storage disabled */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [tenant, setTenantState] = useState(() => readCachedTenant());
  const [ready, setReady] = useState(false);

  const setTenant = useCallback((t) => {
    setTenantState(t);
    writeCachedTenant(t);
  }, []);

  const refreshTenant = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/me");
      setTenant(data);
      return data;
    } catch {
      return null;
    }
  }, [setTenant]);

  useEffect(() => {
    api.get("/auth/me")
      .then(async (r) => {
        setUser(r.data);
        if (r.data?.tenant_id) await refreshTenant();
      })
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, [refreshTenant]);

  // /auth/me carries the role's effective permissions for this workspace.
  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; } catch { return null; }
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    clearCache();
    if (data.token) localStorage.setItem("tp_token", data.token);
    setUser(data);
    await Promise.all([refreshTenant(), refreshUser()]);
    return data;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) localStorage.setItem("tp_token", data.token);
    setUser(data);
    await Promise.all([refreshTenant(), refreshUser()]);
    return data;
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("tp_token");
    clearCache();
    setUser(false);
    setTenant(null);
  };

  return (
    <AuthCtx.Provider value={{ user, tenant, ready, login, register, logout, refreshTenant, refreshUser, setTenant }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
